import * as angular from 'angular';
import * as moment from 'moment';
import * as _ from 'lodash';

import { GroupNameDto, ILogbookModalConfiguration, ImportResult, ImportResultDto, IStringDateRange, IValueViewModel, LogbookEntry, LogbookEntryDto, LogbookWidgetFilters, ReprocessingTaskOptionsOverride, SaveLogbookDto } from '../../models';
import { IRequestService, IUploadService } from '../';
import { toLogbookEntry } from '../../common/helpers/logBookEntryConverter';
import { BadgeVM } from '../../widgets';
import { IIdentifierVmService } from '../vm';
import { IAuthenticationService, ILoggerService } from '../general';
import { addLogbookEntryModalId, dashboardValuesUrl, Roles } from '../../common/constants';
import { logbookModalRefresh } from '../../common/events';

export interface ILogbookModalSeries {
    vm: IValueViewModel;
    isBoundToGroup: boolean;
}

export interface ILogbookService {
    getMultiLineTitle(entries: LogbookEntryDto[]): string;
    getMultiLineTitleHtml(entries: LogbookEntryDto[]): string;
    getDtoBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<LogbookEntryDto[]>;
    getBetweenDates(beginDate: string, endDate: string): angular.IPromise<LogbookEntry[]>;
    getBetweenDates(beginDate: string, endDate: string, force: boolean): angular.IPromise<LogbookEntry[]>;
    getBetweenDatesWithFilter(beginDate: string, endDate: string, filters: LogbookWidgetFilters, force: boolean): angular.IPromise<LogbookEntry[]>;
    deleteEntry(id: number, deleteValues: boolean, isAsync: boolean): angular.IPromise<void>;
    deleteEntries(id: number[]): angular.IPromise<void>;
    getAllLabels(): angular.IPromise<{ labelId: number, name: string }[]>;
    add(dto: SaveLogbookDto, taskOptionsOverride?: ReprocessingTaskOptionsOverride): angular.IPromise<LogbookEntryDto>;
    update(dtos: SaveLogbookDto[], taskOptionsOverride?: ReprocessingTaskOptionsOverride): angular.IPromise<LogbookEntryDto[]>;
    getBadge(entries: LogbookEntryDto[], timestamp: string | Date): BadgeVM;
    getEntriesRegarding(entries: LogbookEntryDto[], guid: string): LogbookEntryDto[];
    openModal(
        serieGuid: string,
        badge: BadgeVM,
        edit: boolean,
        hasManualInputRole: boolean,
        showLogbook: boolean,
        range: IStringDateRange,
        severity?: number,
        group?: GroupNameDto,
        prefilters?: LogbookWidgetFilters,
        allowedGroupGuidList?: string[],
        series?: ILogbookModalSeries[],
        taskOptionsOverride?: ReprocessingTaskOptionsOverride,
        isAsync?: boolean
    ): void;
    upload(file: File): angular.IPromise<ImportResult>;
}

export class LogbookService implements ILogbookService {
    static serviceId = 'logbookService';

    private _controllerUrl = 'logbook';

    private readonly _titleTranslated: string;
    private readonly _multiTitle: string;

    static $inject = [
        '$q',
        '$rootScope',
        '$translate',
        'requestService',
        'authenticationService',
        'identifierVmService',
        'loggerService',
        'uploadService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$rootScope: angular.IRootScopeService,
        private _$translate: angular.translate.ITranslateService,
        private _requestService: IRequestService,
        private _authenticationService: IAuthenticationService,
        private _identifierVmService: IIdentifierVmService,
        private _loggerService: ILoggerService,
        private _uploadService: IUploadService
    ) {
        this._titleTranslated = this._$translate.instant('Logbook entries');
        this._multiTitle = '_comment_\n_beginDateTime_.._endDateTime_\nCreated by _user_\nGroup: _group.uniqueName_\nCategories: _categories_';
    }

    private cleanCaches(): void {
        this._requestService.cleanCachedPosts(dashboardValuesUrl);
        this._requestService.cleanCacheAtV1(this._controllerUrl);
        this._requestService.cleanCacheAtV1(this._controllerUrl + '/labels');
    }

    getMultiLineTitle(entries: LogbookEntryDto[]): string {
        const sortedEntries = entries.sort((a, b) => a.orderId < b.orderId ? -1 : 1);
        return sortedEntries.map(e => this._$translate.instant(this._multiTitle, e))
            .reduce((a, b) => `${a}\n\n${b}`);
    }

    getMultiLineTitleHtml(entries: LogbookEntryDto[]): string {
        let title = this.getMultiLineTitle(entries);
        title = `<tspan>${title.split('\n').join('</tspan>\n<tspan>')}</tspan>`;
        return title;
    }

    getDtoBetweenDates(beginDate: string, endDate: string): angular.IPromise<LogbookEntryDto[]>;
    getDtoBetweenDates(beginDate: string, endDate: string, force: boolean): angular.IPromise<LogbookEntryDto[]>;
    getDtoBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<LogbookEntryDto[]> {
        if (force) {
            this._requestService.cleanCacheAtV1(this._controllerUrl, { beginDateTime: beginDate, endDateTime: endDate });
        }
        return this._requestService
            .getAtV1<LogbookEntryDto[]>(this._controllerUrl, { beginDateTime: beginDate, endDateTime: endDate }, true);
    }

    getBetweenDates(beginDate: string, endDate: string): angular.IPromise<LogbookEntry[]>;
    getBetweenDates(beginDate: string, endDate: string, force: boolean): angular.IPromise<LogbookEntry[]>;
    getBetweenDates(beginDate: string, endDate: string, force?: boolean): angular.IPromise<LogbookEntry[]> {
        return this.getDtoBetweenDates(beginDate, endDate, force)
            .then(data => data.map(toLogbookEntry));
    }

    getBetweenDatesWithFilter(beginDate: string, endDate: string, filters: LogbookWidgetFilters, force: boolean = false): angular.IPromise<LogbookEntry[]> {
        if (force) {
            this._requestService.cleanCachedPostValue('logbook/getWithFilters', { beginDate, endDate, filters });
        }
        return this._requestService
            .cachedPostAtV1<LogbookEntryDto[]>('logbook/getWithFilters', { beginDate, endDate, filters }, undefined, true)
            .then(data => data.map(toLogbookEntry));
    }


    deleteEntry(id: number, isDeleteValues: boolean, isAsync: boolean): angular.IPromise<void> {
        return this._requestService
            .postAtV1(`logbook/${id}/${isDeleteValues}/${isAsync}`, undefined, undefined, true)
            .then(() => this.cleanCaches());
    }

    deleteEntries(ids: number[]): angular.IPromise<void> {
        return this._requestService
            .postAtV1(`logbook/delete`, ids, undefined, true)
            .then(() => this.cleanCaches());
    }

    getAllLabels(): angular.IPromise<{ labelId: number; name: string }[]> {
        return this._requestService.getAtV1<{ labelId: number; name: string }[]>(`logbook/labels`, undefined, true);
    }

    add(dto: SaveLogbookDto, taskOptionsOverride: ReprocessingTaskOptionsOverride = null): angular.IPromise<LogbookEntryDto> {
        return this._requestService
            .postAtV1<LogbookEntryDto>(`logbook/create`, { values: [dto], taskOptionsOverride }, undefined, true)
            .then(data => {
                this.cleanCaches();
                return data;
            });
    }

    update(dtos: SaveLogbookDto[], taskOptionsOverride: ReprocessingTaskOptionsOverride = null): angular.IPromise<LogbookEntryDto[]> {
        return this._requestService
            .postAtV1<LogbookEntryDto[]>(`logbook/update`, { values: dtos, taskOptionsOverride }, undefined, true)
            .then(data => {
                this.cleanCaches();
                return data;
            });
    }

    // Take entries related to the same serie at a timestamp and build a badge
    getBadge(entries: LogbookEntryDto[], timestamp: string | Date): BadgeVM {
        let badge: BadgeVM = { hidden: true, add: true, severity: -1 };

        if (entries.length > 0) {
            const date = moment(timestamp);
            const inRanges = entries
                .filter(r => r.range)
                .filter(r => date.isBetween(r.range.startDate, r.range.endDate, null, '[)')
                    || date >= moment(r.range.startDate) && !r.range.endDate);
            if (inRanges.length > 0) {
                let title: string;
                let content: string;
                if (inRanges.length === 1) {
                    title = this.getMultiLineTitleHtml([inRanges[0]]);
                    content = inRanges[0].orderId.toString();
                } else {
                    title = `${this._titleTranslated}: ${inRanges.map(r => r.orderId).join(', ')}`;
                    content = '';
                }

                badge = {
                    content: content,
                    title: title,
                    severity: _.maxBy(inRanges, r => r.severity).severity,
                    entries: inRanges
                };
            }
        }

        return badge;
    }

    getEntriesRegarding(entries: LogbookEntryDto[], guid: string): LogbookEntryDto[] {
        const logbookEntries = entries
            ? entries.filter(e => e.identifier && e.identifier.guid === guid)
            : [];
        logbookEntries.filter(e => !e.range).forEach(e => {
            e.range = { startDate: moment(e.beginDateTime), endDate: moment(e.endDateTime) };
        });
        return logbookEntries;
    }

    openModal(
        serieGuid: string,
        badge: BadgeVM,
        edit: boolean,
        hasManualInputRole: boolean,
        showLogbook: boolean,
        range: IStringDateRange,
        severity?: number,
        group?: GroupNameDto,
        prefilters?: LogbookWidgetFilters,
        allowedGroupGuids?: string[],
        series?: ILogbookModalSeries[],
        taskOptionsOverride: ReprocessingTaskOptionsOverride = null,
        isAsync: boolean = true
    ): void {
        const hasMultipleEntries = badge.entries?.length > 1;
        if (!hasManualInputRole || !showLogbook || hasMultipleEntries) {
            // do nothing on cell if no or multiple logbook entries
            // or no right to edit
            return;
        }
        const user = this._authenticationService.authentication;
        const entry = edit ? badge.entries[0] : undefined;
        if (edit) {
            const canEdit = (entry.userName === user.userName || user.roles.indexOf(Roles.master) >= 0);
            if (!canEdit) {
                this._loggerService.logWarning(`Cannot edit logbook entry by ${entry.user}.`, true);
                return;
            }
            // override provided range to edit with entry range
            range = undefined;
        }
        badge.isLoading = true;

        const promise: angular.IPromise<{ group: GroupNameDto; guid: string; }> = (group || (!group && !serieGuid))
            ? this._$q.when({ group: group, guid: serieGuid })
            : this._identifierVmService
                .getSomeViewModels([serieGuid])
                .then(vmList => {
                    if (!vmList[0]) {
                        throw `Serie (${serieGuid}) not found.`;
                    }
                    const vm = vmList[0];
                    const group = vm.groupList[0];
                    return { group, guid: vm.guid };
                });

        promise
            .then(cfg => {
                return {
                    allowedGroupGuids,
                    entry,
                    group: cfg.group,
                    identifierGuid: cfg.guid,
                    isAsync,
                    prefilters,
                    range,
                    series,
                    severity,
                    taskOptionsOverride
                } as ILogbookModalConfiguration;
            })
            .then(param => {
                $(`#${addLogbookEntryModalId}`).modal('show');
                this._$rootScope.$broadcast(logbookModalRefresh, param);
            })
            .finally(() => badge.isLoading = false);
    }

    upload(file: File): angular.IPromise<ImportResult> {
        const url = `${this._requestService.getUrlV1()}${this._controllerUrl}/upload`;
        return this._uploadService.uploadFile<ImportResultDto<LogbookEntryDto>>(url, file)
            .then(data => {
                return {
                    result: data.result,
                    createdCount: data.created.length,
                    updatedCount: data.updated.length,
                    failedCount: data.notUpdated.length
                };
            });
    }
}